import React from "react";
import styled from "styled-components";
import { colors } from "utils";
import { Tooltip } from "components/shared/Tooltip/Tooltip";

const parseIso = (iso) => {
  const date = new Date(String(iso).slice(0, 10) + "T00:00:00");
  return Number.isNaN(date.getTime()) ? null : date;
};

// "2025-08-01" -> "2025–26": a school year runs Aug 1 to Jul 31.
export const schoolYearLabel = (iso) => {
  const date = parseIso(iso);
  if (!date) return iso === null || iso === undefined ? "" : String(iso);
  const year = date.getFullYear();
  return `${year}–${String(year + 1).slice(2)}`;
};

export const periodLabel = (iso, trendBy) => {
  const date = parseIso(iso);
  if (!date) return String(iso);
  if (trendBy === "school_year") return schoolYearLabel(iso);
  if (trendBy === "month") {
    const month = date.toLocaleDateString("en-US", { month: "short" });
    return `${month} ’${String(date.getFullYear()).slice(2)}`;
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

/**
 * Sums `count(row)` per tier into one entry per period, oldest first. Rows
 * with no period are skipped; the server never zero-fills, so a period with
 * no rows has no column.
 */
export const periodsFromRows = (rows, trendBy, tiers, count) => {
  const byPeriod = new Map();
  rows.forEach((row) => {
    if (!row.period_of) return;
    const key = String(row.period_of);
    const counts = byPeriod.get(key) || Object.fromEntries(tiers.map((t) => [t.key, 0]));
    tiers.forEach((t) => {
      counts[t.key] += count(row, t.key);
    });
    byPeriod.set(key, counts);
  });
  return [...byPeriod.keys()].sort().map((key) => ({
    key,
    label: periodLabel(key, trendBy),
    counts: byPeriod.get(key),
  }));
};

/**
 * Brick-style stacked bar chart of counts per period, colored by tier
 * (Junko's design). Bricks are sized off a shared "nice" y-axis maximum so
 * periods compare visually; the gap shrinks when a period carries too many
 * records for 3px seams.
 *
 * `tiers` is the top-to-bottom stack order: [{ key, color, short?, name }].
 * The key reads the other way, Lv1 first, with unlevelled tiers last.
 * `periods` is [{ key, label, counts: { [tierKey]: n } }], already sorted.
 * Each tier in a column names its level and count on hover or focus.
 */
export const TrendBrickChart = ({ title, yLabel, tiers, periods }) => {
  if (periods.length === 0) return null;

  const totals = periods.map((p) => tiers.reduce((sum, t) => sum + (p.counts[t.key] || 0), 0));
  const max = Math.max(...totals, 1);
  // Round up to a multiple of 4 so the quarter ticks are whole numbers.
  const niceMax = Math.max(4, Math.ceil(max / 4) * 4);
  // One DOM node per record only works at classroom scale. Past the limit
  // (district-wide ranges), each tier collapses to one solid proportional
  // block — same colors, same read, bounded node count.
  const solid = max > BRICK_LIMIT;
  const gap = solid ? 2 : 3;
  const ticks = [niceMax, (niceMax * 3) / 4, niceMax / 2, niceMax / 4, 0];

  return (
    <ChartCard>
      <ChartTitle>{title}</ChartTitle>
      <ChartBody>
        <AxisLabel>{yLabel}</AxisLabel>
        <TickColumn>
          {ticks.map((tick) => (
            <Tick key={tick}>{tick}</Tick>
          ))}
        </TickColumn>
        <PlotArea>
          <GridLines>
            <GridLine style={{ top: "25%" }} />
            <GridLine style={{ top: "50%" }} />
            <GridLine style={{ top: "75%" }} />
          </GridLines>
          {periods.map((period) => (
            <PeriodColumn key={period.key}>
              <Bricks>
                {tiers.map((tier) => {
                  const count = period.counts[tier.key] || 0;
                  if (count === 0) return null;
                  const bricks = solid ? 1 : count;
                  const label = `${tierLabel(tier)}, ${yLabel}: ${count.toLocaleString()}`;
                  return (
                    <Tooltip
                      key={tier.key}
                      placement="right"
                      nowrap
                      hiddenFromScreenReaders
                      tip={
                        <>
                          {tierLabel(tier)}
                          <br />
                          {yLabel}: {count.toLocaleString()}
                        </>
                      }
                    >
                      <Tier
                        role="img"
                        aria-label={label}
                        tabIndex={0}
                        $height={(CHART_HEIGHT * count) / niceMax}
                      >
                        {Array.from({ length: bricks }, (_, i) => (
                          <Brick
                            key={i}
                            $color={tier.color}
                            $heightPct={100 / bricks}
                            $gap={gap}
                          />
                        ))}
                      </Tier>
                    </Tooltip>
                  );
                })}
              </Bricks>
              <PeriodLabel>{period.label}</PeriodLabel>
            </PeriodColumn>
          ))}
        </PlotArea>
      </ChartBody>
      <Key>
        <KeyTitle>Key:</KeyTitle>
        <KeyItems>
          {keyOrder(tiers).map(({ key, color, short, name }) => (
            <KeyItem key={key}>
              <Swatch $color={color} />
              <KeyText>
                {"= "}
                {short && <strong>{`${short}:`}</strong>}
                {short ? ` ${name}` : name}
              </KeyText>
            </KeyItem>
          ))}
        </KeyItems>
      </Key>
    </ChartCard>
  );
};

const keyOrder = (tiers) => [
  ...tiers.filter((t) => t.short).reverse(),
  ...tiers.filter((t) => !t.short),
];

// "Lv1" + "Needs Support" -> "Level 1: Needs Support".
const tierLabel = ({ short, name }) =>
  short ? `${short.replace(/^Lv/, "Level ")}: ${name}` : name;

const CHART_HEIGHT = 268;
export const BRICK_LIMIT = 60;

const ChartCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0 8px;
`;

const ChartTitle = styled.h2`
  font-family: "ABC Marfa", "Plus Jakarta Sans", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #002e19;
  text-align: center;
  margin: 0;
`;

const ChartBody = styled.div`
  display: flex;
  align-items: flex-start;
`;

const AxisLabel = styled.div`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002e19;
  height: ${CHART_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TickColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  height: ${CHART_HEIGHT}px;
  padding: 0 12px 0 8px;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  color: #002e19;

  /* Center each tick label on its gridline. */
  > * {
    transform: translateY(-50%);
  }
  > *:first-child {
    transform: none;
  }
  > *:last-child {
    transform: translateY(30%);
  }
`;

const Tick = styled.div`
  line-height: 22px;
`;

const PlotArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  align-items: flex-start;
`;

const GridLines = styled.div`
  position: absolute;
  inset: 0 0 auto 0;
  height: ${CHART_HEIGHT}px;
  pointer-events: none;
`;

const GridLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  border-top: 1px dashed #858b87;
`;

const PeriodColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const Bricks = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: ${CHART_HEIGHT}px;
  width: 70%;
  max-width: 82px;
  border-bottom: 1px solid #030712;
  box-sizing: content-box;
`;

// Sized in px, not %, because the tooltip's wrapper sits between the tier and
// the column, and a % height has nothing definite to resolve against there.
const Tier = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: ${(p) => p.$height}px;
  border-radius: 4px;

  &:focus-visible {
    outline: 2px solid #605dec;
    outline-offset: 2px;
  }
`;

const Brick = styled.div`
  height: calc(${(p) => p.$heightPct}% - ${(p) => p.$gap}px);
  margin-bottom: ${(p) => p.$gap}px;
  border-radius: 4px;
  background-color: ${(p) => p.$color};
`;

const PeriodLabel = styled.div`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002e19;
  white-space: nowrap;
`;

// Same key as the Student report's collaboration chart.
const Key = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const KeyTitle = styled.p`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const KeyItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 32px;
  row-gap: 8px;
`;

const KeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Swatch = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  background-color: ${(p) => p.$color};
`;

const KeyText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  color: ${colors.nightlyWoods500};

  strong {
    font-weight: 700;
  }
`;
